import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AppShell from '../layouts/AppShell';
import AdminLayout from '../layouts/AdminLayout';
import { API } from '../App';
import UserRosterCard from '../components/UserRosterCard';
import { hasPermission, hasAnyPermission } from '../utils/permissionUtils';
import { buildNavigationItems } from '../utils/navigationConfig';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';

const UserDirectoryPage = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionUpdateBusyUserId, setPositionUpdateBusyUserId] = useState(null);
  const [error, setError] = useState('');
  const [confirmToggle, setConfirmToggle] = useState({
    open: false,
    userId: null,
    userName: '',
    nextState: true,
  });
  const [chatToggle, setChatToggle] = useState({
    open: false,
    userId: null,
    userName: '',
    nextState: true,
  });
  const [chatToggleLoadingId, setChatToggleLoadingId] = useState(null);
  const [resetModal, setResetModal] = useState({
    open: false,
    userId: null,
    userName: '',
    newPassword: '',
    confirmPassword: '',
    submitting: false
  });
  const [editModal, setEditModal] = useState({
    open: false,
    userId: null,
    initialPositionId: '__none__',
    form: { name: '', email: '', contactNumber: '', country: '', empId: '', role: 'agent', positionId: '__none__' },
    submitting: false
  });
  const navigate = useNavigate();

  const canManageTemplates = useMemo(() => hasPermission(user, 'template:manage'), [user]);
  const canManageIntegrations = useMemo(() => hasPermission(user, 'integration:manage'), [user]);
  const canManagePositions = useMemo(() => hasPermission(user, 'position:manage'), [user]);
  const canManageTeams = useMemo(() => hasPermission(user, 'team:manage'), [user]);
  const canViewUserRoster = useMemo(
    () => hasAnyPermission(user, ['position:assign', 'position:manage', 'user:manage']),
    [user]
  );
  const canAssignPositions = useMemo(() => hasPermission(user, 'position:assign'), [user]);
  const canManageUsers = useMemo(() => hasPermission(user, 'user:manage'), [user]);
  const canInviteUsers = useMemo(() => hasPermission(user, 'user:invite'), [user]);
  const canViewStats = useMemo(() => hasPermission(user, 'stats:view'), [user]);
  const canModerateComments = useMemo(() => hasPermission(user, 'comment:moderate'), [user]);

  const navigationItems = useMemo(
    () =>
      buildNavigationItems({
        canManageTemplates,
        canViewUserRoster,
        canManagePositions,
        canManageTeams,
        canInviteUsers,
        canViewStats,
        canManageIntegrations,
        canModerateComments
      }),
    [
      canManageTemplates,
      canViewUserRoster,
      canManagePositions,
      canManageTeams,
      canInviteUsers,
      canViewStats,
      canManageIntegrations,
      canModerateComments
    ]
  );

  const loadUserRoster = useCallback(async () => {
    if (!canViewUserRoster) {
      setUsers([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(`${API}/users/roster`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      setUsers(response.data || []);
    } catch (loadError) {
      console.error('Error loading user roster:', loadError);
      setError(loadError.response?.data?.detail || loadError.message || 'Unable to load users');
    } finally {
      setLoading(false);
    }
  }, [canViewUserRoster]);

  const loadPositions = useCallback(async () => {
    if (!canAssignPositions) {
      setPositions([]);
      return;
    }
    setPositionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(`${API}/positions`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      setPositions(response.data || []);
    } catch (positionsError) {
      console.error('Error loading positions:', positionsError);
    } finally {
      setPositionsLoading(false);
    }
  }, [canAssignPositions]);

  useEffect(() => {
    loadUserRoster();
  }, [loadUserRoster]);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  const handleToggleActive = useCallback(
    (userId, isActive, userName) => {
      setConfirmToggle({
        open: true,
        userId,
        userName: userName || 'this user',
        nextState: isActive,
      });
    },
    []
  );

  const handleToggleReceiveChats = useCallback(
    (userId, nextState, userName) => {
      setChatToggle({
        open: true,
        userId,
        userName: userName || 'this user',
        nextState,
      });
    },
    []
  );

  const confirmToggleAction = useCallback(async () => {
    if (!confirmToggle.userId) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.patch(
        `${API}/users/${confirmToggle.userId}/active`,
        { is_active: confirmToggle.nextState },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      setConfirmToggle({ open: false, userId: null, userName: '', nextState: true });
      await loadUserRoster();
    } catch (toggleError) {
      console.error('Error updating user status:', toggleError);
      setError(toggleError.response?.data?.detail || toggleError.message || 'Unable to update user');
      setConfirmToggle({ open: false, userId: null, userName: '', nextState: true });
    }
  }, [confirmToggle, loadUserRoster]);

  const confirmChatToggleAction = useCallback(async () => {
    if (!chatToggle.userId) return;
    setChatToggleLoadingId(chatToggle.userId);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.patch(
        `${API}/users/${chatToggle.userId}/chat-routing`,
        { can_receive_new_chats: chatToggle.nextState },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      setError('');
      setChatToggle({ open: false, userId: null, userName: '', nextState: true });
      await loadUserRoster();
    } catch (toggleError) {
      console.error('Error updating chat routing:', toggleError);
      setError(
        toggleError.response?.data?.detail ||
          toggleError.message ||
          'Unable to update chat routing'
      );
      setChatToggle((prev) => ({ ...prev, open: false }));
      await loadUserRoster();
    } finally {
      setChatToggleLoadingId(null);
    }
  }, [chatToggle, loadUserRoster]);

  const handleAssignPosition = useCallback(
    async (userId, positionId) => {
      if (!canAssignPositions) {
        return;
      }
      setError('');
      setPositionUpdateBusyUserId(userId);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }
        await axios.post(
          `${API}/users/${userId}/position`,
          { position_id: positionId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        await loadUserRoster();
      } catch (assignError) {
        console.error('Error assigning position:', assignError);
        setError(assignError.response?.data?.detail || assignError.message || 'Unable to update position');
      } finally {
        setPositionUpdateBusyUserId(null);
      }
    },
    [canAssignPositions, loadUserRoster]
  );

  const openEditModal = useCallback((selectedUser) => {
    setEditModal({
      open: true,
      userId: selectedUser.id,
      initialPositionId: selectedUser.position?.id || '__none__',
      form: {
        name: selectedUser.name || '',
        email: selectedUser.email || '',
        contactNumber: selectedUser.contact_number || '',
        country: selectedUser.country || '',
        empId: selectedUser.emp_id || '',
        role: selectedUser.role || 'agent',
        positionId: selectedUser.position?.id || '__none__'
      },
      submitting: false
    });
  }, []);

  const closeEditModal = useCallback(() => {
    setEditModal((current) => ({ ...current, open: false, submitting: false }));
  }, []);

  const updateEditField = (field, value) => {
    setEditModal((current) => ({ ...current, form: { ...current.form, [field]: value } }));
  };

  const saveEditedUser = useCallback(async () => {
    if (!editModal.userId || !editModal.form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setEditModal((current) => ({ ...current, submitting: true }));
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/users/${editModal.userId}`, {
        name: editModal.form.name.trim(),
        email: editModal.form.email.trim() || null,
        contact_number: editModal.form.contactNumber.trim() || null,
        country: editModal.form.country.trim() || null,
        emp_id: editModal.form.empId.trim() || null,
        role: editModal.form.role
      }, { headers, timeout: 30000 });
      if (canAssignPositions && editModal.userId !== user.id && editModal.form.positionId !== editModal.initialPositionId) {
        await axios.post(`${API}/users/${editModal.userId}/position`, {
          position_id: editModal.form.positionId === '__none__' ? null : editModal.form.positionId
        }, { headers, timeout: 30000 });
      }
      setError('');
      closeEditModal();
      await loadUserRoster();
    } catch (saveError) {
      console.error('Error editing user:', saveError);
      setError(saveError.response?.data?.detail || saveError.message || 'Unable to save user changes');
      setEditModal((current) => ({ ...current, submitting: false }));
    }
  }, [canAssignPositions, closeEditModal, editModal, loadUserRoster, user.id]);

  const openResetModal = useCallback((user) => {
    setResetModal({
      open: true,
      userId: user.id,
      userName: user.name || 'User',
      newPassword: '',
      confirmPassword: '',
      submitting: false
    });
  }, []);

  const closeResetModal = () => {
    setResetModal((prev) => ({ ...prev, open: false, newPassword: '', confirmPassword: '', submitting: false }));
  };

  const handleResetPassword = useCallback(async () => {
    if (!resetModal.userId) return;
    if (!resetModal.newPassword || resetModal.newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (resetModal.newPassword !== resetModal.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setResetModal((prev) => ({ ...prev, submitting: true }));
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.post(
        `${API}/admin/users/${resetModal.userId}/reset-password`,
        { new_password: resetModal.newPassword },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
      );
      setError('');
      closeResetModal();
      await loadUserRoster();
    } catch (resetError) {
      console.error('Error resetting password:', resetError);
      setError(resetError.response?.data?.detail || resetError.message || 'Unable to reset password');
      setResetModal((prev) => ({ ...prev, submitting: false }));
    }
  }, [loadUserRoster, resetModal]);

  return (
    <AppShell user={user} onLogout={onLogout} navItems={navigationItems}>
      <AdminLayout
        title="User Directory"
        description="Monitor your team, spot workload spikes, and jump into assignments."
      >
        <div className="flex-1 overflow-y-auto space-y-4">
          <AlertDialog open={confirmToggle.open} onOpenChange={(open) => !open && setConfirmToggle({ open: false, userId: null, userName: '', nextState: true })}>
            <AlertDialogContent className="bg-[var(--tg-surface)] border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
              <AlertDialogHeader>
                <AlertDialogTitle>{confirmToggle.nextState ? 'Activate user?' : 'Deactivate user?'}</AlertDialogTitle>
                <AlertDialogDescription className="text-[var(--tg-text-muted)]">
                  Are you sure you want to {confirmToggle.nextState ? 'activate' : 'deactivate'}{' '}
                  <strong>{confirmToggle.userName}</strong>?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmToggleAction}
                  className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white"
                >
                  Yes, {confirmToggle.nextState ? 'activate' : 'deactivate'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={chatToggle.open} onOpenChange={(open) => !open && setChatToggle({ open: false, userId: null, userName: '', nextState: true })}>
            <AlertDialogContent className="bg-[var(--tg-surface)] border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {chatToggle.nextState ? 'Allow new chat assignments?' : 'Pause new chat assignments?'}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[var(--tg-text-muted)]">
                  {chatToggle.nextState
                    ? 'Allow this user to start receiving new chat assignments again? They must also have Send Messages permission.'
                    : 'Pause new chat assignments for this user? They will stop receiving new chats until you enable this again.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmChatToggleAction}
                  disabled={!!chatToggleLoadingId}
                  className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white"
                >
                  {chatToggleLoadingId
                    ? 'Saving...'
                    : chatToggle.nextState
                    ? 'Yes, allow'
                    : 'Yes, pause'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={resetModal.open} onOpenChange={(open) => !open && closeResetModal()}>
            <AlertDialogContent className="bg-[var(--tg-surface)] border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset password</AlertDialogTitle>
                <AlertDialogDescription className="text-[var(--tg-text-muted)]">
                  Set a new password for <strong>{resetModal.userName}</strong>. This action takes effect immediately.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs text-[var(--tg-text-secondary)]">New password</label>
                  <Input
                    type="password"
                    value={resetModal.newPassword}
                    onChange={(e) => setResetModal((prev) => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    minLength={8}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[var(--tg-text-secondary)]">Confirm new password</label>
                  <Input
                    type="password"
                    value={resetModal.confirmPassword}
                    onChange={(e) => setResetModal((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Repeat password"
                    autoComplete="new-password"
                    minLength={8}
                  />
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleResetPassword}
                  disabled={resetModal.submitting}
                  className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white"
                >
                  {resetModal.submitting ? 'Setting...' : 'Set password'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog open={editModal.open} onOpenChange={(open) => !open && closeEditModal()}>
            <DialogContent className="border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] text-[var(--tg-text-primary)]">
              <DialogHeader>
                <DialogTitle>Edit user</DialogTitle>
                <DialogDescription className="text-[var(--tg-text-muted)]">Update this account's details and position.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1"><Label htmlFor="edit-user-name">Full name</Label><Input id="edit-user-name" value={editModal.form.name} onChange={(event) => updateEditField('name', event.target.value)} /></div>
                <div className="space-y-1"><Label htmlFor="edit-user-email">Email</Label><Input id="edit-user-email" type="email" value={editModal.form.email} onChange={(event) => updateEditField('email', event.target.value)} /></div>
                <div className="space-y-1"><Label htmlFor="edit-user-contact">Contact number</Label><Input id="edit-user-contact" value={editModal.form.contactNumber} onChange={(event) => updateEditField('contactNumber', event.target.value)} /></div>
                <div className="space-y-1"><Label htmlFor="edit-user-country">Country</Label><Input id="edit-user-country" value={editModal.form.country} onChange={(event) => updateEditField('country', event.target.value)} /></div>
                <div className="space-y-1"><Label htmlFor="edit-user-emp-id">Employee ID</Label><Input id="edit-user-emp-id" value={editModal.form.empId} onChange={(event) => updateEditField('empId', event.target.value)} /></div>
                <div className="space-y-1"><Label htmlFor="edit-user-role">Role</Label><select id="edit-user-role" value={editModal.form.role} onChange={(event) => updateEditField('role', event.target.value)} className="h-10 w-full rounded-md border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] px-3 text-sm"><option value="agent">Agent</option><option value="admin">Admin</option></select></div>
                {canAssignPositions && editModal.userId !== user.id && <div className="space-y-1 sm:col-span-2"><Label htmlFor="edit-user-position">Position</Label><select id="edit-user-position" value={editModal.form.positionId} onChange={(event) => updateEditField('positionId', event.target.value)} disabled={positionsLoading} className="h-10 w-full rounded-md border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] px-3 text-sm"><option value="__none__">No position</option>{positions.map((position) => <option key={position.id} value={position.id}>{position.name}</option>)}</select></div>}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeEditModal} disabled={editModal.submitting}>Cancel</Button>
                <Button type="button" onClick={saveEditedUser} disabled={editModal.submitting}>{editModal.submitting ? 'Saving...' : 'Save changes'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {error && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}
          {canViewUserRoster ? (
            <UserRosterCard
              users={users}
              loading={loading}
              canManagePositions={canManagePositions}
              currentUserId={user.id}
              canAssignPositions={canAssignPositions}
              positions={positions}
              positionsLoading={positionsLoading}
              positionUpdateBusyUserId={positionUpdateBusyUserId}
              onManagePositions={() => navigate('/positions')}
              onAssignPosition={handleAssignPosition}
              onToggleActive={handleToggleActive}
              canToggleActive={canManageUsers}
              onToggleReceiveChats={handleToggleReceiveChats}
              canToggleReceiveChats={canManageUsers}
              chatToggleBusyUserId={chatToggleLoadingId}
              canResetPasswords={canManageUsers}
              onResetPassword={openResetModal}
              canEditUsers={canManageUsers}
              onEditUser={openEditModal}
            />
          ) : (
            <div className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-6 text-sm text-[var(--tg-text-secondary)]">
              You do not have permission to view the user directory.
            </div>
          )}
        </div>
      </AdminLayout>
    </AppShell>
  );
};

export default UserDirectoryPage;
